/**
 * @file   src\components\Onboarding\Dashboard\LeftpanelTabContentRight.js
 * @brief  Match tab left panel - property selector + demand insights
 * @date   April, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useEffect, useState, useContext, useMemo } from "react";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { filterContext } from "../../../pages/v2/Home";
import { USER_TYPE_AGENT, BUY } from "../../../constants/common";
import {
  getLocalStorage,
  setLocalStorage,
  removeLocalStorage,
  decodeParam,
} from "../../../helpers/common";
import { USER_INFO_KEY } from "../../../constants/common";
import { usePrevious } from "../../../hooks/usePrevious";
import { getMatchClientList } from "../../../services/agentClientsService";
import { renameKey, combineKey } from "../../../helpers/common";
import { getInventoryListByUserId } from "../../../services/inventoryService";
import { fetchUserDetails } from "../../../actions/userprofile";
import { useSearchParams } from "react-router-dom";
import AddProperty from "./AddProperty";
import PropertySelectorDropdown from "./PropertySelectorDropdown";
import MatchDemandPanel from "./MatchDemandPanel";
import { getMyMlsListings } from "../../../services/v2/mlsListings";
import { mlsPropertyId } from "../../../helpers/matchTabRedirect";
import { useAppConfig } from "@/appConfig";

const LeftpanelTabContentRight = (props) => {
  const dispatch = useDispatch();
  const filterCntxt = useContext(filterContext);
  const [searchParams] = useSearchParams();
  const invIdfromUrl = searchParams.get("InvId");
  const clientIdFromUrl = searchParams.get("ClientId");

  const decodedInvId = invIdfromUrl ? decodeParam(invIdfromUrl) : null;
  const decodedClientId = clientIdFromUrl ? decodeParam(clientIdFromUrl) : null;

  const previouslySelectedClient = getLocalStorage("MatchSelectedClient");
  const previouslySelectedProperty = getLocalStorage("selectedPropertyId");
  // Match tab demand insights - read match data from filterContext
  const { adType, adListGitchaGenie, totalCountMatch, updateQueryGitchaGenie } = filterCntxt;
  const sessionData = getLocalStorage(USER_INFO_KEY);
  const userType = _.get(sessionData, "userInfo.userType", "");
  const userId = _.get(sessionData, "userInfo.userId", "");
  const [propertyList, setPropertyList] = useState(null);
  const [mlsPropertyList, setMlsPropertyList] = useState(null);
  const [hasFetchedProperties, setHasFetchedProperties] = useState(false);
  const [hasFetchedMls, setHasFetchedMls] = useState(false);
  const { flags } = useAppConfig();
  const showMlsListings = !!flags?.mlsListings;
  const [clientList, setClientList] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const userDetailsFromStore = useSelector(
    (state) => state.userProfile.userDetails,
  );
  const [userDetails, setUserDetails] = useState(null);
  const { updateFilterGitchaGenie, selectedProperty, setSelectedProperty } =
    props;
  const key = getLocalStorage("FilterTabKey");
  const [isPremiumUser, setIsPremiumUser] = useState(null);

  const prevAdType = usePrevious(adType);
  const prevInventoryId = usePrevious(selectedProperty?.Inv_Id);
  const [reload, setReload] = useState(0);

  const allProperties = useMemo(() => {
    const merged = [...(propertyList || []), ...(mlsPropertyList || [])];
    return merged.length ? merged : null;
  }, [propertyList, mlsPropertyList]);

  useEffect(() => {
    getClientList();
    if (userId) {
      dispatch(fetchUserDetails());
    }
  }, [key, adType, reload]);

  useEffect(() => {
    if (userDetailsFromStore !== null && userDetailsFromStore?.length > 0) {
      setUserDetails(userDetailsFromStore[0]);
      if (userDetailsFromStore[0]?.period !== 1 && userType === 2) {
        setIsPremiumUser(true);
        setLocalStorage("isPremiumUser", 2); // 2 for premium
      } else {
        setIsPremiumUser(false);
        setLocalStorage("isPremiumUser", 1); // 1 for non premium
      }
    } else {
      setUserDetails(null);
    }
  }, [userDetailsFromStore]);

  const updateReloadParam = () => {
    setReload(reload + 1);
  };

  useEffect(() => {
    if (selectedClient) {
      setLocalStorage("MatchSelectedClient", selectedClient);
      getInventoryList();
    }
  }, [adType, selectedClient]);

  useEffect(() => {
    if (clientList && clientList?.length > 0) {
      if (decodedClientId) {
        const clientFromUrl = clientList?.find(
          (client) => client.value == decodedClientId,
        );
        if (clientFromUrl) {
          setSelectedClient(clientFromUrl);
        } else {
          setSelectedClient(clientList[0]);
        }
      } else if (previouslySelectedClient) {
        const previousClient = clientList?.find(
          (client) => client.value == previouslySelectedClient.value,
        );
        if (previousClient) {
          setSelectedClient(previousClient);
        } else {
          setSelectedClient(clientList[0]);
        }
      } else {
        setSelectedClient(clientList[0]);
      }
    }
  }, [clientList, decodedClientId]);

  useEffect(() => {
    if (prevInventoryId != selectedProperty?.Inv_Id || adType != prevAdType) {
      setLocalStorage("selectedPropertyId", selectedProperty);
      const isMls = selectedProperty?.rowType === "mls";
      updateFilterGitchaGenie({
        inventoryId: isMls ? null : selectedProperty?.Inv_Id,
        mlsListingKey: isMls ? selectedProperty?.ListingKey : null,
        city: selectedProperty?.City,
        state: selectedProperty?.State,
      });
    }
  }, [selectedProperty]);

  useEffect(() => {
    const allFetched =
      hasFetchedProperties && (!showMlsListings || hasFetchedMls);
    if (allProperties && allProperties.length > 0) {
      if (decodedInvId != null) {
        const invFromUrl = allProperties.find(
          (property) => property.Inv_Id == decodedInvId,
        );
        if (invFromUrl) setSelectedProperty(invFromUrl);
        else if (allFetched) setSelectedProperty(allProperties[0]);
      } else if (previouslySelectedProperty) {
        const previousProperty = allProperties.find(
          (property) => property.Inv_Id == previouslySelectedProperty.Inv_Id,
        );
        if (previousProperty) setSelectedProperty(previousProperty);
        else if (allFetched) setSelectedProperty(allProperties[0]);
      } else if (allFetched) {
        setSelectedProperty(allProperties[0]);
      }
    } else if (allFetched) {
      removeLocalStorage("selectedPropertyId");
      setSelectedProperty(null);
    }
  }, [
    allProperties,
    decodedInvId,
    hasFetchedProperties,
    hasFetchedMls,
    showMlsListings,
  ]);

  /**
   * To fetch inventory details
   */
  const getInventoryList = async () => {
    if (userId) {
      await getInventoryListByUserId({ adType })
        .then((response) => {
          const responseResult = _.get(response, "result", null);
          if (response.errorCode === 0 && responseResult) {
            if (responseResult.length > 0) {
              setPropertyList(responseResult);
            } else {
              setPropertyList(null);
            }
          }
          setHasFetchedProperties(true);
        })
        .catch((error) => {
          console.error("[Match] inventory error:", error);
          setHasFetchedProperties(true);
        });
    }
  };
  useEffect(() => {
    if (userId && key === "match" && showMlsListings && adType === BUY) {
      getMlsProperties();
    } else {
      setMlsPropertyList(null);
      setHasFetchedMls(true);
    }
  }, [key, adType, showMlsListings, userId]);

  const getMlsProperties = async () => {
    await getMyMlsListings({ page: 1, limit: 100, sortType: "NEWEST" })
      .then((response) => {
        const rows = _.get(response, "data.data", []);
        const adapted = (rows || []).map((r) => ({
          ...r,
          rowType: "mls",
          Inv_Id: mlsPropertyId(r.ListingKey),
        }));
        setMlsPropertyList(adapted.length ? adapted : null);
      })
      .catch((error) => {
        console.error("[Match] mls listings error:", error);
      })
      .finally(() => {
        setHasFetchedMls(true);
      });
  };

  /**
   * To fetch client details for agents
   */
  const getClientList = async () => {
    if (userId && key == "match") {
      await getMatchClientList()
        .then((response) => {
          const responseResult = _.get(response, "result", null);
          if (response.errorCode === 0 && responseResult) {
            responseResult?.ClientList.forEach((obj) =>
              renameKey(obj, "ClientId", "value"),
            );
            responseResult?.ClientList.forEach((obj) =>
              combineKey(obj, "FirstName", "LastName", "label"),
            );
            setClientList(responseResult?.ClientList);
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    }
  };

  return (
    <>
      <div className="filter-properties-wrap pb-20">
        <h6>My Properties</h6>
        {allProperties ? (
          <PropertySelectorDropdown
            propertyList={allProperties}
            selectedProperty={selectedProperty}
            setSelectedProperty={setSelectedProperty}
            adType={adType}
            userType={userType}
            selectedClient={selectedClient}
            userDetails={userDetails}
            updateReloadParam={updateReloadParam}
          />
        ) : (
          <AddProperty
            isAgent={userType === USER_TYPE_AGENT}
            selectedClient={selectedClient}
            userDetails={userDetails}
          />
        )}
      </div>

      {/* Match tab demand insights - replaces dual-handle price slider */}
      <div className="filter-properties-wrap mt-3 border-bottom-0">
        <h6>Demand Insights</h6>
        <MatchDemandPanel
          selectedProperty={selectedProperty}
          adType={adType}
          totalCountMatch={totalCountMatch}
          adListGitchaGenie={adListGitchaGenie}
          onPricePointChange={(pricePoint) => updateQueryGitchaGenie({ pricePoint, page: 1 })}
        />
      </div>
    </>
  );
};
export default LeftpanelTabContentRight;
